import React, { useRef, useState } from 'react';
import styles from './TcsoInventoryCheckScreen.module.css';
import { useTcsoContext, useTcsoScreenContext } from '../../context/TcsoScreenContext';
import { TcsoScreen } from './TcsoScreenManager';
import { useChatReasonContext, useHomePageContext } from '../../context/HomePageContext';
import InventoryCheck, { InventoryCheckRef, InventoryData } from '../inventoryCheck/InventoryCheck';
import { TcsoInventoryResult } from './TcsoInventoryResult';
import { startChatSession } from '../chat/startChatSession';
import { buildChatData } from '../decommission/s1-site/util';
import { getAuthTokenOrLogOut, InventoryCheckReportingRequest } from '../util';
import { TCSO } from '../chat/Chat';
import SmoothProgressBar from '../common/progressBar/SmoothProgressBar';
import BackButton from '../common/button/BackButton';
import { reportingClient } from '../../api/atomProxyClient';

export default function TcsoInventoryCheckScreen() {
  const { setTcsoScreenInputs } = useTcsoScreenContext();
  const { homePageState, setHomePageState } = useHomePageContext();
  const { chatReason, setChatReason } = useChatReasonContext();
  const { tcsoInputs, setTcsoInputs } = useTcsoContext();

  const [inventoryCheckStarted, setInventoryCheckStarted] = useState(false);
  const [inventoryCheckCompleted, setInventoryCheckCompleted] = useState(false);
  const [inventoryData, setInventoryData] = useState<InventoryData>({
    address: '',
    data: [],
    state: null,
  });

  const inventoryCheckRef = useRef<InventoryCheckRef>(null);
  const requestedMpxnCheckRef = useRef<string>('');
  const reportingData = useRef<any>({
    journeyId: '',
    status: '',
    startingTimeStamp: new Date(Date.parse('1 Jan, 2000')),
  });

  const startInventoryCheck = () => {
    inventoryCheckRef.current?.trigger();
  };

  const retryInventoryCheck = () => {
    setInventoryCheckCompleted(false);
    setInventoryCheckStarted(false);
  };

  const goBack = () => {
    setChatReason('');
    setHomePageState({
      ...homePageState,
      automationInProgress: false,
    });
    setTcsoScreenInputs({
      tcsoScreen: TcsoScreen.TCSO_CHECKLIST_SCREEN,
    });
    setInventoryCheckStarted(false);
    setInventoryCheckCompleted(false);
  };

  const cancelInventoryCheck = () => {
    setChatReason(TCSO);
    setHomePageState({
      ...homePageState,
      automationInProgress: false,
    });
    setTcsoScreenInputs({
      tcsoScreen: TcsoScreen.TCSO_CHECKLIST_SCREEN,
    });
    setInventoryCheckStarted(false);
    setInventoryCheckCompleted(false);
  };

  const onInventoryCheckStarted = (mpxn: string) => {
    requestedMpxnCheckRef.current = mpxn;
    setInventoryCheckStarted(true);
    setInventoryCheckCompleted(false);
    reportingData.current.startingTimeStamp = new Date();
    const journeyId = crypto.randomUUID();
    reportingData.current.journeyId = journeyId;
    setTcsoInputs({
      journeyId: journeyId,
      ...tcsoInputs,
    });
  };

  const onInventoryCheckCompleted = async (invData: InventoryData) => {
    setInventoryCheckStarted(false);
    setInventoryCheckCompleted(true);
    setInventoryData(invData);

    const chfId = invData.data.find((device) => device.deviceType === 'CHF')?.deviceId ?? '';
    const gpfId = invData.data.find((device) => device.deviceType === 'GPF')?.deviceId ?? '';

    setTcsoInputs({
      mpxn: requestedMpxnCheckRef.current,
      chfDeviceId: chfId,
      gpfDeviceId: gpfId,
      address: invData.address,
      journeyId: reportingData.current.journeyId,
    });

    const userDetails = await getAuthTokenOrLogOut();
    const now = new Date();
    const request: InventoryCheckReportingRequest = {
      decommissionJourneyId: reportingData.current.journeyId,
      mpxn: requestedMpxnCheckRef.current,
      contactReason: chatReason,
      engineerEmail: userDetails.userEmail ?? '',
      inventoryCheckResultStatus: invData.state,
      inventoryCheckStartingTimeStamp: reportingData.current.startingTimeStamp.toISOString(),
      inventoryCheckResultTimeStamp: now.toISOString(),
      inventoryCheckTimeTaken: now.getTime() - reportingData.current.startingTimeStamp.getTime(),
    };

    reportingClient.inventoryCheck(request).then();
  };

  const startChatHandler = async () => {
    const chatData = buildChatData(inventoryData, requestedMpxnCheckRef.current, TCSO);
    await startChatSession({ message: chatData, mpxn: requestedMpxnCheckRef.current, chatReason });
  };

  const failScreenCancelHandler = async () => {
    setInventoryCheckStarted(false);
    setInventoryCheckCompleted(false);
    setTcsoScreenInputs({
      tcsoScreen: TcsoScreen.TCSO_INVENTORY_CHECK_SCREEN,
    });
  };

  const renderCompletedInventoryCheckButton = () => {
    const confirmAddressButton = (
      <button
        className={`${styles.tcsoConfirmButton} ${styles.bgcGreen} ${styles.mGB8}`}
        onClick={() => {
          setTcsoScreenInputs({
            tcsoScreen: TcsoScreen.TCSO_START_SCREEN,
          });
        }}
      >
        Confirm Address
      </button>
    );

    const startChatButton = (
      <button className={`${styles.tcsoButton} ${styles.bgcGreen} ${styles.mGB8}`} onClick={startChatHandler}>
        Start Chat
      </button>
    );

    const warningSmets1 = (
      <div className={'innerWarningTextContainer'}>Smets1 device is not supported. Please contact SST for support.</div>
    );

    const warningUnknownSmetsVersion = (
      <div className={'innerWarningTextContainer'}>Unable to determine smets version. Please contact SST for support.</div>
    );

    const warningNotCommissioned = (
      <div className={'innerWarningTextContainer'}>Device is not commissioned. Please contact SST for support.</div>
    );

    const warningGasExists = (
      <div className={'innerWarningTextContainer'}>Gas Meters are not supported. Please contact SST for support.</div>
    );

    const devices = inventoryData.data.filter((device) => device.deviceType !== 'PPMID');
    if (inventoryCheckCompleted && inventoryData.state === 'SUCCESS') {
      if (devices.find((device) => device.smetsDeviceType === 'Smets 1')) {
        return (
          <>
            <div className={'outerWarningContainer'}>
              {warningSmets1}
              {startChatButton}
            </div>
          </>
        );
      } else if (devices.find((device) => device.smetsDeviceType === 'Unknown')) {
        return (
          <>
            <div className={'outerWarningContainer'}>
              {warningUnknownSmetsVersion}
              {startChatButton}
            </div>
          </>
        );
      } else if (
        devices.find(
          (device) => device.deviceType === 'GSME' || (device.deviceStatus === 'COMMISSIONED' && device.deviceType === 'GPF'),
        )
      ) {
        return (
          <>
            <div className={'outerWarningContainer'}>
              {warningGasExists}
              {startChatButton}
            </div>
          </>
        );
      } else if (
        devices.find(
          (device) =>
            (device.deviceType !== 'GPF' && device.deviceStatus !== 'COMMISSIONED') ||
            (device.deviceType === 'GPF' && device.deviceStatus !== 'INSTALLED_NOT_COMMISSIONED'),
        )
      ) {
        return (
          <>
            <div className={'outerWarningContainer'}>
              {warningNotCommissioned}
              {startChatButton}
            </div>
          </>
        );
      } else {
        return confirmAddressButton;
      }
    }
  };

  return (
    <div className="tcsoScreenContainer">
      <div className={'tcsoBody'}>
        {inventoryCheckStarted && <SmoothProgressBar durationMinutes={3} />}
        <div className={`${styles.tcsoInventoryResultsScroll} ${inventoryCheckStarted ? '' : 'm-t-8'}`}>
          <BackButton onClick={goBack} />
          {!(
            !inventoryCheckStarted &&
            inventoryCheckCompleted &&
            (inventoryData.state === 'FAIL' || inventoryData.state === 'PROCESSING')
          ) && (
            <InventoryCheck
              onStarted={onInventoryCheckStarted}
              onCompleted={onInventoryCheckCompleted}
              triggerRef={inventoryCheckRef}
              inputMpxn={tcsoInputs.mpxn}
            />
          )}

          <div className={`${styles.overAuto} ${styles.inventoryCheckScreenContainer}`}>
            {inventoryCheckStarted && (
              <div className={styles.waitingScreen}>
                <div className={'bodyTitleText'}>Finding your site details</div>
                <div className={'bodyText'}>This may take 3 minutes, please wait.</div>
              </div>
            )}

            {inventoryCheckCompleted && <TcsoInventoryResult inventoryData={inventoryData} retry={retryInventoryCheck} />}
          </div>
        </div>
      </div>
      <div className={'tcsoFooter'}>
        {!inventoryCheckStarted && !inventoryCheckCompleted && (
          <button className={'tcsoButton'} onClick={startInventoryCheck}>
            Search
          </button>
        )}

        {inventoryCheckStarted && (
          <button className={'tcsoButton'} onClick={cancelInventoryCheck}>
            Cancel
          </button>
        )}

        {inventoryCheckCompleted && (inventoryData.state === 'FAIL' || inventoryData.state === 'PROCESSING') && (
          <>
            <button className={`${styles.tcsoTimeoutButton} ${styles.bgcGreen} ${styles.mGB8}`} onClick={startChatHandler}>
              Start Chat
            </button>
            <button className={`${styles.tcsoTimeoutButton} ${styles.bgcDefault}`} onClick={failScreenCancelHandler}>
              Cancel
            </button>
          </>
        )}

        {renderCompletedInventoryCheckButton()}
      </div>
    </div>
  );
}
